import React, { useMemo } from 'react';

import { UnavailableFooter } from 'features/menu/components/menu-item-unavailable/unavailable-footer';
import { getCaloriesAndPrice } from 'features/menu/components/menu-tile-grid/menu-tile/helpers';
import { useCalories } from 'features/menu/components/menu-tile-grid/menu-tile/hooks';
import { IMenuTile } from 'features/menu/components/menu-tile-grid/menu-tile/types';
import Tile from 'features/menu/components/menu-tile-grid/tile';
import { TileLinkExternal } from 'features/menu/components/menu-tile-grid/tile-link';
import {
  IMenuObjectWithContentOptions,
  useGetVariantContentOptions,
} from 'features/menu/hooks/use-get-variant-content-options';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { StyleSheet, View } from 'react-native';

import Link from 'components/link';
import { MenuObjectTypes } from 'enums/menu';
import { ClickEventComponentNames } from 'state/amplitude/constants';
import { primitive, spacing } from 'styles/constants';
import { isExternalLink } from 'utils/is-external-link';
import { optionToUrl } from 'utils/menu';
import { startTrace } from 'utils/performance/trace';
import { TraceName } from 'utils/performance/types';

const SECTION_TILE_STRING = 'section';

// OLD (not one menu)
export const MenuTile = React.memo<IMenuTile>(
  ({
    item,
    objectFitContain = false,
    deeplinkUnavailable = false,
    offerStyleCard = false,
    onUnavailableChooseRestaurant,
  }) => {
    const getCalories = useCalories();

    const { calories, price } = useMemo(
      // we always enable the menu service data here because this is the "new" menu-tile
      () => getCaloriesAndPrice(item, getCalories),
      [getCalories, item]
    );

    const getVariantContentOptions = useGetVariantContentOptions();
    const { description, image, name } = getVariantContentOptions(
      item as IMenuObjectWithContentOptions
    );

    const tile = (
      <Tile
        hideCalories={!!item.hideCalories}
        calories={calories}
        description={description?.localeRaw}
        id={item._id}
        image={image}
        imageDescription={item.imageDescription?.locale}
        name={name?.locale ?? ''}
        objectFitContain={objectFitContain}
        price={price}
        type={item._type}
        accessibilityLabel={name?.locale ?? ''}
        accessibilityRole="header"
        accessibilityLevel={3}
        deeplinkUnavailable={deeplinkUnavailable}
      />
    );

    if (deeplinkUnavailable) {
      return (
        <View style={styles.unavailableCard} testID="menu-section-unavailable-item">
          {tile}
          <UnavailableFooter onPress={onUnavailableChooseRestaurant} />
        </View>
      );
    }

    const redirectUrl = item._type === MenuObjectTypes.SECTION && item?.redirectLink?.locale;

    if (redirectUrl && isExternalLink(redirectUrl)) {
      const external = (
        <TileLinkExternal key={item._id} href={redirectUrl}>
          {tile}
        </TileLinkExternal>
      );
      return offerStyleCard ? <View style={styles.availableCard}>{external}</View> : external;
    }

    const sanityId = item._id;
    const linkPath = redirectUrl || optionToUrl(item);

    const logClickEvent = (tileType: string, productId: string, productName: string) => {
      const isSection = tileType === SECTION_TILE_STRING;
      const attributes = {
        component: isSection
          ? ClickEventComponentNames.PRODUCT_SECTION_TILES
          : ClickEventComponentNames.PRODUCTS,
        name: productName,
        componentId: productId,
      };

      logButtonClick({ attributes });

      startTrace(isSection ? TraceName.MENU_SECTION_LOAD : TraceName.MENU_PDP_LOAD, {
        menuItemId: productId,
      });
    };

    const handleLinkClick = () => {
      logClickEvent(item._type, item._id, item.name?.locale);
    };

    const linked = (
      <Link
        key={sanityId}
        onPress={handleLinkClick}
        linkPath={linkPath}
        dd-action-name={item.name?.locale}
      >
        {tile}
      </Link>
    );

    return offerStyleCard ? <View style={styles.availableCard}>{linked}</View> : linked;
  }
);

const styles = StyleSheet.create({
  availableCard: {
    backgroundColor: primitive.$white,
    borderCurve: 'continuous',
    borderRadius: 10,
    marginBottom: 10,
    marginHorizontal: spacing.$4,
    overflow: 'hidden',
  },
  unavailableCard: {
    borderCurve: 'continuous',
    borderRadius: 10,
    marginBottom: 10,
    marginHorizontal: spacing.$4,
    marginTop: spacing.$2,
    overflow: 'hidden',
  },
});
